import axios from 'axios';

// Resolve API base URL for Create React App
// - Development: Empty string uses CRA's proxy to forward requests to backend
// - Production: Set REACT_APP_API_URL environment variable for cross-origin API
const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_TIMEOUT_MS = 15_000; // 15 seconds

// SSR-safe localStorage adapter
const hasWindow = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
const storage = {
  getItem(key) {
    try {
      return hasWindow ? window.localStorage.getItem(key) : null;
    } catch (error) {
      console.error(`apiClient:storage.getItem failed for key "${key}"`, error);
      return null;
    }
  },
  setItem(key, value) {
    try {
      if (hasWindow) window.localStorage.setItem(key, value);
    } catch (error) {
      console.error(`apiClient:storage.setItem failed for key "${key}"`, error);
    }
  },
  removeItem(key) {
    try {
      if (hasWindow) window.localStorage.removeItem(key);
    } catch (error) {
      console.error(`apiClient:storage.removeItem failed for key "${key}"`, error);
    }
  }
};

// Centralized Axios instance with auth & refresh handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS
});

// Separate client for refresh (no interceptors to avoid recursion)
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS
});

// Header helpers compatible with Axios v1 and plain objects
function setHeader(headers, name, value) {
  if (headers && typeof headers.set === 'function') headers.set(name, value);
  else headers[name] = value;
}
function hasHeader(headers, name) {
  if (!headers) return false;
  if (typeof headers.has === 'function') return headers.has(name);
  const lower = name.toLowerCase();
  return Object.keys(headers).some((k) => k.toLowerCase() === lower);
}
function deleteHeader(headers, name) {
  if (!headers) return;
  if (typeof headers.delete === 'function') headers.delete(name);
  else {
    const lower = name.toLowerCase();
    Object.keys(headers).forEach((k) => {
      if (k.toLowerCase() === lower) delete headers[k];
    });
  }
}

function maybeStripContentTypeForFormData(config) {
  const isFormData =
    typeof FormData !== 'undefined' && config?.data instanceof FormData;
  if (isFormData) {
    deleteHeader(config.headers, 'Content-Type');
  }
}

// Attach Authorization header from storage; keep Content-Type flexible
api.interceptors.request.use((config) => {
  // Axios handles JSON content-type automatically; don't force it globally.
  maybeStripContentTypeForFormData(config);

  const token = storage.getItem('access_token');
  if (token && !hasHeader(config.headers, 'Authorization')) {
    // eslint-disable-next-line no-param-reassign
    config.headers = config.headers || {};
    setHeader(config.headers, 'Authorization', `Bearer ${token}`);
  }
  return config;
});

let isRefreshing = false;
let refreshQueue = [];

function subscribeTokenRefresh(cb) {
  refreshQueue.push(cb);
}

function onRefreshed(token) {
  refreshQueue.forEach((cb) => cb(token, null));
  refreshQueue = [];
}

function onRefreshFailed(err) {
  refreshQueue.forEach((cb) => cb(null, err));
  refreshQueue = [];
}

async function refreshAccessToken() {
  const refresh = storage.getItem('refresh_token');
  if (!refresh) throw new Error('No refresh token');
  const res = await refreshClient.post('/api/auth/token/refresh/', { refresh });
  const data = res?.data || {};
  const newAccess = data.access || data.access_token || data.accessToken;
  const newRefresh = data.refresh || data.refresh_token || data.refreshToken;
  if (!newAccess) throw new Error('No access token in refresh response');
  storage.setItem('access_token', newAccess);
  if (newRefresh) storage.setItem('refresh_token', newRefresh);
  return newAccess;
}

// Refresh access tokens for protected endpoints, including /api/auth/me and
// /api/auth/admin/*, but exclude credential-issuing/revocation endpoints.
const AUTH_REFRESH_EXCLUDED_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/token/refresh',
  '/api/auth/logout'
]);

function getRequestPath(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const base = API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return new URL(url, base).pathname;
  } catch (_error) {
    return url.split('?')[0].split('#')[0];
  }
}

function isRefreshExcludedEndpoint(url) {
  const path = getRequestPath(url).replace(/\/+$/, '');
  return AUTH_REFRESH_EXCLUDED_PATHS.has(path);
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const skipRefresh = isRefreshExcludedEndpoint(original?.url);

    if (status === 401 && !original?._retry && !skipRefresh) {
      // eslint-disable-next-line no-param-reassign
      original._retry = true; // prevent loops
      if (!isRefreshing) {
        // Start a refresh and, once done, retry this very request immediately
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          onRefreshed(newToken);
          original.headers = original.headers || {};
          setHeader(original.headers, 'Authorization', `Bearer ${newToken}`);
          return api(original);
        } catch (e) {
          isRefreshing = false;
          onRefreshFailed(e);
          // Clear tokens and surface an auth event
          storage.removeItem('access_token');
          storage.removeItem('refresh_token');
          storage.removeItem('user');
          try {
            if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
              window.dispatchEvent(new CustomEvent('auth:logout'));
            }
          } catch (error) {
            console.error('apiClient: failed to dispatch auth:logout event', error);
          }
          // Reject with the refresh error so callers can differentiate
          return Promise.reject(e);
        }
      }

      // Refresh already in progress: wait for it, then retry or fail
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token, err) => {
          if (err) {
            reject(err);
            return;
          }
          try {
            original.headers = original.headers || {};
            setHeader(original.headers, 'Authorization', `Bearer ${token}`);
            resolve(api(original));
          } catch (e) {
            reject(e);
          }
        });
      });
    }

    return Promise.reject(error);
  }
);

/**
 * User-friendly error messages in Dutch
 * Maps common error types to helpful messages with guidance
 */
const ERROR_MESSAGES = {
  // Network and connection errors
  NETWORK_ERROR: 'Kan geen verbinding maken met de server. Controleer je internetverbinding en probeer het opnieuw.',
  TIMEOUT_ERROR: 'Het verzoek duurde te lang. Controleer je internetverbinding en probeer het opnieuw.',

  // Generic fallback
  REQUEST_FAILED: 'Het verzoek kon niet worden verwerkt. Probeer het later opnieuw.',

  // Server errors
  SERVER_ERROR: 'Er is een serverfout opgetreden. Probeer het later opnieuw.'
};

/**
 * Detects if an error is a network or connection error.
 * Uses multiple detection methods to work across browsers.
 */
function isNetworkError(err) {
  // Check Axios-specific error codes
  if (err?.code === 'ERR_NETWORK' || err?.code === 'ECONNREFUSED' || err?.code === 'ENOTFOUND') {
    return true;
  }
  // Check if there's no response (typical of network failures)
  if (!err?.response && err?.request) {
    return true;
  }
  // Check error message patterns (fallback)
  const message = err?.message?.toLowerCase() || '';
  return message.includes('network') || message.includes('failed to fetch');
}

/**
 * Detects if an error is a timeout error.
 */
function isTimeoutError(err) {
  return err?.code === 'ECONNABORTED' ||
         err?.message?.toLowerCase().includes('timeout');
}

/**
 * Get a user-friendly error message from an error object.
 * Prioritizes server-provided messages, falls back to helpful defaults.
 * Options: fallbackMessage, statusMessages, fieldPriority.
 */
export function getErrorMessage(err, options = {}) {
  const normalizedOptions = typeof options === 'string'
    ? { fallbackMessage: options }
    : (options || {});
  const fallbackMessage = normalizedOptions.fallbackMessage;
  const statusMessages = normalizedOptions.statusMessages || {};
  const fieldPriority = Array.isArray(normalizedOptions.fieldPriority)
    ? normalizedOptions.fieldPriority
    : [];

  try {
    const status = err?.response?.status;
    if (status !== undefined && status !== null) {
      const statusOverride = statusMessages[status] || statusMessages[String(status)];
      if (typeof statusOverride === 'string' && statusOverride.trim() !== '') {
        return statusOverride;
      }
    }

    // Handle network errors (no response from server)
    if (!err?.response) {
      if (isTimeoutError(err)) {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
      if (isNetworkError(err)) {
        return ERROR_MESSAGES.NETWORK_ERROR;
      }
    }

    const data = err?.response?.data;

    // Handle specific HTTP status codes with user-friendly messages
    if (status === 500 || status === 502 || status === 503) {
      // Server errors - use server message if available, otherwise generic
      if (data && typeof data === 'object') {
        const serverMsg = data.detail || data.error || data.message;
        if (serverMsg && typeof serverMsg === 'string') {
          return serverMsg;
        }
      }
      return ERROR_MESSAGES.SERVER_ERROR;
    }

    if (!data) return err?.message || fallbackMessage || ERROR_MESSAGES.REQUEST_FAILED;
    if (typeof data === 'string') return data;
    if (Array.isArray(data)) return data.filter(Boolean).join(', ');

    // Prioritized common fields from server response
    const prioritized = data.detail || data.error || data.message || data.errors;
    if (typeof prioritized === 'string') return prioritized;
    if (Array.isArray(prioritized)) return prioritized.filter(Boolean).join(', ');

    if (fieldPriority.length > 0) {
      for (const key of fieldPriority) {
        const val = data[key];
        if (typeof val === 'string') return val;
        if (Array.isArray(val)) return val.filter(Boolean).join(', ');
      }
    }

    // Django/DRF style field errors: { field: ["msg"], non_field_errors: [...] }
    const nonField = data.non_field_errors || data.nonFieldErrors;
    if (Array.isArray(nonField)) return nonField.filter(Boolean).join(', ');

    // Pick the first string/array value from any field
    for (const key of Object.keys(data)) {
      const val = data[key];
      if (typeof val === 'string') return val;
      if (Array.isArray(val)) return val.filter(Boolean).join(', ');
    }

    return err?.message || fallbackMessage || ERROR_MESSAGES.REQUEST_FAILED;
  } catch (error) {
    console.error('apiClient:getErrorMessage encountered parsing error', error);
    return err?.message || fallbackMessage || ERROR_MESSAGES.REQUEST_FAILED;
  }
}

export { storage, ERROR_MESSAGES, isNetworkError, isTimeoutError };
export default api;
